import { Injectable } from '@angular/core';
import { EventService } from './event.service';
import { EventTypes } from '../models/events.model';
import {
  DeviceSignals,
  isWeakDevice,
  readDeviceSignals,
  withBudget,
} from './device-capability.util';

/** Why the heavy on-device stack was skipped (mirrors `context.reason`). */
export type OnDeviceSkipReason =
  | 'static_pregate'
  | 'load_timeout'
  | 'load_error'
  | 'capture_budget';

/** Claim/KYC linkage so emitted telemetry attaches to the attempt. */
export interface KycEventContext {
  claimId?: string | null;
  kycCheckId?: string | null;
}

/**
 * Decides whether the SomosKycV1 guided-capture flow should run its heavy
 * on-device stack (ONNX classify/segment, Tesseract OCR, brdocs preprocess) on
 * this device, and skips it gracefully when the device can't keep up.
 *
 * Two triggers:
 *  - a **static pre-gate** on `navigator.deviceMemory` / `hardwareConcurrency`
 *    ({@link evaluate}), and
 *  - a **time budget** around each heavy step ({@link guardLoad} /
 *    {@link guardCapture}) that latches heavy processing off on overrun or error.
 *
 * Once disabled it stays disabled for the session (no point re-trying the slow
 * path each capture), and a single {@link EventTypes.KYC_ONDEVICE_PROCESSING_SKIPPED}
 * event is emitted with the reason. The cheap live framing guide is unaffected —
 * only the heavy per-capture work is gated.
 */
@Injectable({ providedIn: 'root' })
export class DeviceCapabilityService {
  /** Budget for the one-time model pre-warm (ONNX + OCR worker), ms. */
  static readonly LOAD_BUDGET_MS = 12000;
  /** Budget for the heavy per-capture processing, ms. */
  static readonly CAPTURE_BUDGET_MS = 8000;

  private heavyEnabled = true;
  private emitted = false;
  private signals: DeviceSignals = readDeviceSignals();
  private ctx: KycEventContext = {};

  constructor(private events: EventService) {}

  /** Attach claim/KYC context so emitted telemetry links to the attempt. */
  setKycContext(ctx: KycEventContext): void {
    this.ctx = ctx ?? {};
  }

  /** Whether the heavy on-device stack should run. */
  get heavyProcessingEnabled(): boolean {
    return this.heavyEnabled;
  }

  /**
   * Sample device signals and apply the static pre-gate. Call once when the
   * camera opens, before pre-warming the models. Returns whether heavy
   * processing is (still) enabled.
   */
  evaluate(): boolean {
    this.signals = readDeviceSignals();
    if (isWeakDevice(this.signals)) {
      this.disable('static_pregate', 0);
    }
    return this.heavyEnabled;
  }

  /** Run the heavy model pre-warm within the load budget. */
  guardLoad<T>(fn: () => Promise<T>): Promise<T | null> {
    return this.guard(fn, DeviceCapabilityService.LOAD_BUDGET_MS, 'load_timeout', 'load_error');
  }

  /** Run heavy per-capture work within the capture budget. */
  guardCapture<T>(fn: () => Promise<T>): Promise<T | null> {
    return this.guard(fn, DeviceCapabilityService.CAPTURE_BUDGET_MS, 'capture_budget', 'capture_budget');
  }

  private async guard<T>(
    fn: () => Promise<T>,
    ms: number,
    timeoutReason: OnDeviceSkipReason,
    errorReason: OnDeviceSkipReason,
  ): Promise<T | null> {
    if (!this.heavyEnabled) return null;
    const outcome = await withBudget(fn, ms);
    if (outcome.ok) return outcome.value;
    this.disable(outcome.kind === 'timeout' ? timeoutReason : errorReason, outcome.elapsedMs);
    return null;
  }

  /** Latch heavy processing off and emit the skip event once per session. */
  private disable(reason: OnDeviceSkipReason, elapsedMs: number): void {
    this.heavyEnabled = false;
    if (this.emitted) return;
    this.emitted = true;
    this.events
      .sendEventWithCaseId(EventTypes.KYC_ONDEVICE_PROCESSING_SKIPPED, this.ctx.claimId ?? null, {
        reason,
        device_memory: this.signals.deviceMemory,
        cpu_cores: this.signals.cpuCores,
        webgpu: this.signals.webgpu,
        elapsed_ms: Math.round(elapsedMs),
        kyc_check_id: this.ctx.kycCheckId ?? null,
      })
      .catch(() => {
        /* events must never block the flow */
      });
  }
}
